import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Upload, Loader2 } from "lucide-react";
import { api } from "@/App";
import { toast } from "sonner";

function DocumentoUpload({ casoId, eventos, onSuccess, onCancel }) {
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [formData, setFormData] = useState({
    tipo_documento: "correo_pdf",
    fecha_documento: new Date().toISOString().split('T')[0],
    descripcion_breve: "",
    evento_hecho_id: "none"
  });

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 10 * 1024 * 1024) {
        toast.error('El archivo no debe superar 10MB');
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      toast.error('Selecciona un archivo');
      return;
    }

    setUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('tipo_documento', formData.tipo_documento);
      uploadFormData.append('fecha_documento', new Date(formData.fecha_documento).toISOString());
      uploadFormData.append('descripcion_breve', formData.descripcion_breve);
      if (formData.evento_hecho_id && formData.evento_hecho_id !== "none") {
        uploadFormData.append('evento_hecho_id', formData.evento_hecho_id);
      }

      await api.post(`/casos/${casoId}/documentos`, uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Documento subido. La IA está analizando el contenido...');
      onSuccess();
    } catch (error) {
      toast.error('Error subiendo documento');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card data-testid="documento-upload-form">
      <CardHeader>
        <CardTitle>Adjuntar Documento</CardTitle>
        <CardDescription>Sube PDFs, contratos, facturas o correos. La IA extraerá información automáticamente.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="file">Archivo (PDF) *</Label>
            <div className="mt-2">
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Upload className="h-8 w-8 text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600">
                    {file ? file.name : 'Haz clic para seleccionar archivo'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">PDF hasta 10MB</p>
                </div>
                <input
                  id="file"
                  type="file"
                  accept=".pdf"
                  className="hidden"
                  onChange={handleFileChange}
                  data-testid="file-input"
                />
              </label>
            </div>
          </div>

          <div>
            <Label>Tipo de Documento</Label>
            <Select value={formData.tipo_documento} onValueChange={(value) => setFormData({...formData, tipo_documento: value})}>
              <SelectTrigger data-testid="tipo-documento-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="correo_pdf">Correo PDF</SelectItem>
                <SelectItem value="contrato">Contrato</SelectItem>
                <SelectItem value="factura">Factura</SelectItem>
                <SelectItem value="informe">Informe</SelectItem>
                <SelectItem value="otro">Otro</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="fecha">Fecha del Documento *</Label>
            <Input
              id="fecha"
              type="date"
              data-testid="fecha-documento-input"
              value={formData.fecha_documento}
              onChange={(e) => setFormData({...formData, fecha_documento: e.target.value})}
              required
            />
          </div>

          <div>
            <Label htmlFor="descripcion">Descripción Breve *</Label>
            <Textarea
              id="descripcion"
              data-testid="descripcion-documento-textarea"
              value={formData.descripcion_breve}
              onChange={(e) => setFormData({...formData, descripcion_breve: e.target.value})}
              placeholder="Breve descripción del documento"
              rows={3}
              required
            />
          </div>

          <div>
            <Label>Vincular a Hecho (Opcional)</Label>
            <Select value={formData.evento_hecho_id} onValueChange={(value) => setFormData({...formData, evento_hecho_id: value})}>
              <SelectTrigger data-testid="evento-link-select">
                <SelectValue placeholder="Sin vincular" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin vincular</SelectItem>
                {eventos.map(e => (
                  <SelectItem key={e.id} value={e.id}>
                    HECHO {e.numero_hecho}: {e.titulo_evento}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onCancel} className="flex-1" disabled={uploading}>
              Cancelar
            </Button>
            <Button type="submit" className="btn-primary flex-1" disabled={uploading} data-testid="upload-documento-btn">
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Subiendo...
                </>
              ) : (
                'Subir Documento'
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

export default DocumentoUpload;
